import React, { useCallback, useMemo, useState } from 'react';
import { IconButton } from '@mui/material';
import { toast } from 'react-toastify';

import { PLACEMENT } from '@components/CustomTextInput';
import Text, { TEXT_COLOR, TEXT_SIZE, TEXT_WEIGHT } from '@components/Text';
import ResourcesList from '@shared/Resources/shared/components/ResourcesList';
import {
  bulkCreateParagraphs,
  deleteParagraph,
  fetchParagraphs,
  updateParagraph,
} from '@services/paragraphsService';
import { exportParagraphsCSV, parseParagraphsCSV } from '../paragraphsCSV';
import {
  StyledCustomTextInput as HeaderStyledCustomTextInput,
  StyledSearchIcon as HeaderStyledSearchIcon,
} from '@containers/Admin/containers/ResourceManager/components/ResourceHeader/styles';
import { createParagraphsTableConf } from '../config';
import {
  ExportIcon,
  HiddenFileInput,
  ImportIcon,
  ListArea,
  Toolbar,
} from '@shared/Resources/Stimulus/shared/tabStyles';
import { ContentRow, DetailsBody, DetailsPane } from '@shared/Resources/Stimulus/shared/detailsPaneStyles';
import { ParagraphsRoot } from '../styles';
import { useCsvResourceTab } from '@shared/Resources/Stimulus/shared/useCsvResourceTab';
import { TOAST_OPTIONS, toToastMessage } from '@shared/Resources/Stimulus/shared/toastUtils';
import StimulusParagraphDetailsEditor from '@shared/Resources/Stimulus/shared/StimulusParagraphDetailsEditor';
import { applyOptimisticSave } from '@shared/Resources/Stimulus/shared/optimisticSave';
import DeleteConfirmDialog from '@shared/Resources/Stimulus/shared/DeleteConfirmDialog';


const ParagraphsList = () => {
  const [saveLoading, setSaveLoading] = useState(false);

  const {
    filteredItems: filteredParagraphs,
    search,
    setSearch,
    loading,
    selectedItem: selectedParagraph,
    setSelectedItem,
    setItems,
    fileInputRef,
    handleClickImport,
    handleImportCSV,
    handleExportCSV,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    pendingDeleteId,
    handleSelect,
  } = useCsvResourceTab({
    fetchItems: fetchParagraphs,
    parseCsvFile: parseParagraphsCSV,
    bulkCreate: bulkCreateParagraphs,
    exportCsv: exportParagraphsCSV,
    deleteItem: deleteParagraph,
    entityLabel: 'paragraphs',
    enableSelection: true,
    messages: {
      loadFailed: 'Failed to load paragraphs',
      imported: (count) => `Imported ${count} paragraphs`,
      exported: (count) => `Exported ${count} paragraphs`,
      deleted: 'Paragraph deleted',
    },
  });

  const tableConf = useMemo(() => createParagraphsTableConf({ onDelete: handleDelete }), [handleDelete]);

  const handleSaveParagraph = useCallback(
    (input) => applyOptimisticSave({
      selectedItem: selectedParagraph,
      input,
      setLoading: setSaveLoading,
      setItems,
      setSelectedItem,
      updateItem: updateParagraph,
      successMessage: 'Paragraph',
      errorMessage: 'Failed to update paragraph',
      onSuccess: (label) => toast.success(`${label} updated`, TOAST_OPTIONS),
      onError: (err, fallback) => toast.error(toToastMessage(err, fallback), TOAST_OPTIONS),
    }),
    [selectedParagraph, setItems, setSelectedItem]
  );

  const renderToolbar = () => (
    <Toolbar>
      <HeaderStyledCustomTextInput
        placeholder="Search by word in text..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        Icon={() => <HeaderStyledSearchIcon />}
        iconPlacement={PLACEMENT.RIGHT}
        autofocus
        name="paragraphs-search"
        id="paragraphs-search"
      />
      <IconButton
        aria-label="Import from CSV"
        title="Import from CSV"
        onClick={handleClickImport}
        size="small"
      >
        <ImportIcon />
      </IconButton>
      <IconButton aria-label="Download CSV" title="Download CSV" onClick={handleExportCSV} size="small">
        <ExportIcon />
      </IconButton>
      <HiddenFileInput type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} />
    </Toolbar>
  );

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <ParagraphsRoot>
        {renderToolbar()}
        <ContentRow>
          <ListArea>
            <ResourcesList
              items={filteredParagraphs}
              tableConf={tableConf}
              selectedItem={selectedParagraph}
              onSelect={handleSelect}
              showSelectionArrow={false}
            />
          </ListArea>
          <DetailsPane>
            <DetailsBody>
              {!selectedParagraph ? (
                <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DISABLED}>
                  Select a paragraph to view details
                </Text>
              ) : (
                <StimulusParagraphDetailsEditor
                  item={selectedParagraph}
                  onSave={handleSaveParagraph}
                  saving={saveLoading}
                />
              )}
            </DetailsBody>
          </DetailsPane>
        </ContentRow>
      </ParagraphsRoot>
      {pendingDeleteId && (
        <DeleteConfirmDialog
          message="Are you sure you want to delete this paragraph?"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </>
  );
};

export default ParagraphsList;
